import React, { useEffect, useRef, useState } from "react";
import {
	MdVolumeUp,
	MdVolumeMute,
	MdMic,
	MdStop,
	MdArrowBack,
	MdHelpOutline,
	MdPause,
	MdSettings,
	MdTextFields,
	MdBook,
	MdImage,
} from "react-icons/md";
import {
	IoVolumeHigh,
	IoVolumeLow,
	IoStop,
	IoHelpCircleOutline,
	IoHeadset,
	IoArrowBack,
} from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import WordList from "../../../components/pronunciation/WordList";
import Results from "../../../components/pronunciation/Results";

import { useAzure } from "../../../hooks/useAzure";
import { useLiveTutor } from "../../../hooks/LiveTutorProvider";
import { useServer } from "../../../hooks/useServer";

import { determinePercentile } from "../../../utils/Helpers";

interface PronunciationProps {
	text: string;
	phonetic_breakdown?: string;
	definition?: string;
	example?: string;
}

const containerVariants = {
	hidden: {
		opacity: 0,
		y: -20,
	},
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			type: "spring",
			damping: 20,
			stiffness: 100,
		},
	},
	exit: {
		opacity: 0,
		y: -20,
		transition: {
			duration: 0.2,
		},
	},
};

const Pronunciation: React.FC<PronunciationProps> = ({
	text,
	phonetic_breakdown,
	definition,
	example,
}) => {
	const [showSentence, setShowSentence] = useState(false);
	const [showDefinition, setShowDefinition] = useState(false);

	const [imageState, setImageState] = useState<{
		isVisible: boolean;
		isLoading: boolean;
		url?: string;
		sentence?: string;
	}>({
		isVisible: false,
		isLoading: false,
	});
	const [percentile, setPercentile] = useState<number | null>(null);
	const [isPercentileLoading, setIsPercentileLoading] = useState(false);
	const [result, setResult] = useState<{
		words: any[];
		blobUrl: string;
		scores: any;
	}>({ words: [], blobUrl: "", scores: null });

	const [isPlayingSlow, setIsPlayingSlow] = useState(false);
	const [isPlayingFast, setIsPlayingFast] = useState(false);
	const [audioPlaying, setAudioPlaying] = useState(false);
	const textareaRef = useRef<any>(null);
	const audioRef = useRef<HTMLAudioElement>(null);

	const { getGlobalAverage, generateImage } = useServer();
	const { speakTextAsync, stopTextAsync } = useAzure();
	const {
		exerciseInfoRef,
		addActiveState,
		removeActiveState,
		actionHandler,
		handleChat,
		showContinueButton,
		pronunciationResultsRef,
		stepFinishCallbackRef,
	} = useLiveTutor();

	const handleGenerateImage = async () => {
		const currentSentence = text;

		// If same sentence, just toggle visibility
		if (currentSentence === imageState.sentence) {
			setImageState((prev) => ({ ...prev, isVisible: !prev.isVisible }));
			return;
		}

		// Generate new image
		setImageState((prev) => ({ ...prev, isLoading: true, isVisible: true }));

		const signedUrl = await generateImage(currentSentence);

		setImageState({
			isVisible: true,
			isLoading: false,
			url: signedUrl,
			sentence: currentSentence,
		});
	};

	useEffect(() => {
		addActiveState("pronunciation");
		(async () => {
			await handleChat("Read the instruction/question.", false);
		})();

		stepFinishCallbackRef.current = async () => {
			try {
				const { words, scores, recordedBlob } = pronunciationResultsRef.current;

				console.log("words", words);
				console.log("scores", scores);

				// Current text
				const globalAverage = await getGlobalAverage(
					exerciseInfoRef.current.text
				);
				const percentile = determinePercentile(
					scores.pronScore,
					globalAverage.score
				);
				setPercentile(percentile);

				// Set result
				const blobUrl = URL.createObjectURL(recordedBlob);
				setResult({ words, blobUrl, scores });
			} catch (error) {
				console.error("Error setting result", error);
			}
		};

		return () => {
			removeActiveState("pronunciation");
		};
	}, []);

	useEffect(() => {
		// If text changes, then reset everything
		setResult({ words: [], blobUrl: "", scores: null });
		setPercentile(null);
	}, [text]);

	const message = {
		99: "Excellent work! 🏆",
		90: "Keep up the excellent work! 🎯",
		75: "Keep up the good work! 🎉",
		50: "Good effort! 🚀",
		0: "Keep practicing! 🌱",
	} as any;

	return (
		<div className="flex flex-col">
			<main className={`flex-1 p-4`}>
				<div className="flex flex-col justify-between max-w-xl mx-auto">
					{/* Show either results or main content */}
					{isPercentileLoading ? (
						<div className="flex items-center justify-center">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
						</div>
					) : percentile !== null && result ? (
						// Results Overlay
						<div className="flex-grow flex flex-col justify-center text-center">
							<div className="mb-6">
								<WordList words={result.words} />
								<div className="flex justify-center gap-4 my-4">
									<button
										disabled={!result.blobUrl}
										onClick={() => {
											if (audioPlaying) {
												audioRef.current!.pause();
												audioRef.current!.currentTime = 0;
												setAudioPlaying(false);
												return;
											}
											setAudioPlaying(true);
											audioRef.current?.play();
										}}
										className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
									>
										{audioPlaying ? <IoStop /> : <IoHeadset />}
									</button>
									<button
										onClick={() => {
											if (isPlayingSlow) {
												console.log("stopping slow");
												stopTextAsync();
												setIsPlayingSlow(false);
												return;
											}
											setIsPlayingSlow(true);

											speakTextAsync({
												text: text,
												rate: -100,
												cb: () => setIsPlayingSlow(false),
											});
										}}
										className="p-3 rounded-full bg-gray-100 hover:bg-gray-200"
									>
										{isPlayingSlow ? <IoStop /> : <IoVolumeLow />}
									</button>
									<button
										onClick={() => {
											if (isPlayingFast) {
												stopTextAsync();
												setIsPlayingFast(false);
												return;
											}
											setIsPlayingFast(true);
											speakTextAsync({
												text: text,
												cb: () => setIsPlayingFast(false),
											});
										}}
										className="p-3 rounded-full bg-gray-100 hover:bg-gray-200"
									>
										{isPlayingFast ? <IoStop /> : <IoVolumeHigh />}
									</button>
								</div>
								<Results scores={result.scores} />

								{/* Percentile Section with Loading State */}
								<div className="mt-6 flex flex-col items-center">
									<div className="relative">
										<div className="absolute -inset-1.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg blur opacity-30"></div>
										<div className="relative px-8 py-4 bg-white rounded-lg shadow-xl border border-gray-100">
											<div className="flex flex-col items-center gap-2">
												{percentile > 0 ? (
													<>
														<div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
															Better pronunciation than
														</div>
														<div className="flex items-baseline gap-1">
															<span className="text-4xl font-extrabold text-blue-600">
																{percentile}%
															</span>
															<span className="text-lg text-gray-700 font-medium">
																of all speakers
															</span>
														</div>
													</>
												) : null}
												<div className="text-sm text-gray-500 mt-1">
													{message[percentile]}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						// Main Content (Word + Buttons)
						<div className="flex-grow flex flex-col justify-center text-center">
							<p className="text-xl mb-4">Say the following:</p>
							<div className="w-full md:w-4/5 relative mx-auto">
								<textarea
									value={text}
									readOnly
									className="w-full p-6 bg-gray-100 rounded-lg  resize-none min-h-[120px] text-center text-2xl  shadow-inner "
									ref={textareaRef}
								/>

								{/* Playback Buttons */}
								<div>
									<div className="absolute bottom-3 left-0 translate-y-1/2 flex gap-2">
										<button
											onClick={() => {
												if (isPlayingSlow) return;
												setIsPlayingSlow(true);
												speakTextAsync({
													text: text,
													rate: -100,
													cb: () => {
														setIsPlayingSlow(false);
													},
												});
											}}
											className="p-2 rounded-full hover:bg-gray-200"
										>
											{isPlayingSlow ? (
												<MdStop className="text-xl" />
											) : (
												<MdVolumeMute className="text-xl" />
											)}
										</button>
										<button
											onClick={() => {
												if (isPlayingFast) return;
												setIsPlayingFast(true);
												speakTextAsync({
													text: text,
													cb: () => {
														setIsPlayingFast(false);
													},
												});
											}}
											className="p-2 rounded-full hover:bg-gray-200"
										>
											{isPlayingFast ? (
												<MdPause className="text-xl" />
											) : (
												<MdVolumeUp className="text-xl" />
											)}
										</button>
									</div>

									<div className="absolute bottom-3 right-0 translate-y-1/2 flex gap-2">
										{example && (
											<button
												onClick={() => {
													setShowSentence((prev) => !prev);
													setShowDefinition(false);
													setImageState((prev) => ({
														...prev,
														isVisible: false,
													}));
												}}
												className="p-2 rounded-full hover:bg-gray-200"
											>
												<MdTextFields className="text-xl" />
											</button>
										)}

										{definition && (
											<button
												onClick={() => {
													setShowDefinition((prev) => !prev);
													setShowSentence(false);
													setImageState((prev) => ({
														...prev,
														isVisible: false,
													}));
												}}
												className="p-2 rounded-full hover:bg-gray-200"
											>
												<MdBook className="text-xl" />
											</button>
										)}

										{example && (
											<button
												onClick={() => {
													handleGenerateImage();
													setShowSentence(false);
													setShowDefinition(false);
												}}
												className="p-2 rounded-full hover:bg-gray-200"
											>
												<MdImage className="text-xl" />
											</button>
										)}
									</div>
								</div>
							</div>

							{/* Display Areas */}
							{showSentence && (
								<AnimatePresence>
									<motion.div
										className="mb-8"
										variants={containerVariants}
										initial="hidden"
										animate="visible"
										exit="exit"
									>
										<p className="text-md font-medium mt-6">
											Example Sentence:
										</p>
										<motion.div className="p-3 rounded-lg">
											{(() => {
												if (!example) return null;
												const word = text;

												// Use word boundaries \b in the regex to match exact words
												const regex = new RegExp(`\\b(${word})\\b`, "i");
												const parts = example.split(regex);

												return parts.map((part: string, index: number) =>
													part.toLowerCase() === word.toLowerCase() ? (
														<span
															key={index}
															className="bg-yellow-200 font-semibold"
														>
															{part}
														</span>
													) : (
														<span key={index}>{part}</span>
													)
												);
											})()}
										</motion.div>
									</motion.div>
								</AnimatePresence>
							)}

							{/* Definition Display */}
							{showDefinition && (
								<AnimatePresence>
									<motion.div
										className="mb-8"
										variants={containerVariants}
										initial="hidden"
										animate="visible"
										exit="exit"
									>
										<p className="text-md font-medium mt-6">Definition:</p>
										<motion.div className="p-3 bg-gray-50 rounded-lg">
											{definition}
										</motion.div>
									</motion.div>
								</AnimatePresence>
							)}

							{/* Image Display */}
							{imageState.isVisible && (
								<div className="mb-4">
									<p className="text-md font-medium">Image:</p>
									<div className="p-3 bg-gray-50 rounded-lg">
										{imageState.isLoading ? (
											<div className="w-full md:w-60 h-auto mx-auto flex items-center justify-center">
												<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
											</div>
										) : (
											<img
												src={imageState.url}
												alt="Word visualization"
												className="w-60 h-auto rounded-lg mx-auto"
											/>
										)}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</main>
			<audio
				style={{ display: "none" }}
				ref={audioRef}
				src={result.blobUrl || ""}
				onEnded={() => setAudioPlaying(false)}
			/>
		</div>
	);
};

export default Pronunciation;
